import React from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useIsSmallScreen } from '../../../hooks/useIsSmallScreen';
import { SERVICE_COLORS } from '../utils/constants';
import { ChartRow, ComparisonGrouping, monthStartTicks } from '../utils/yearComparison';

interface YearComparisonChartProps {
    rows: ChartRow[];
    years: number[];
    /** The metric's name, for the axis and tooltip */
    metricLabel: string;
    unit?: string;
    grouping: ComparisonGrouping;
}

/** One cumulative line per year over the season */
const YearComparisonChart: React.FC<YearComparisonChartProps> = ({ rows, years, metricLabel, unit, grouping }) => {
    const isSmallScreen = useIsSmallScreen();
    const axisLabel = `${metricLabel}, ackumulerat`;

    return (
        <>
            {isSmallScreen && <p className="analysis-chart-caption">{axisLabel}</p>}
            <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={rows}
                        margin={isSmallScreen ? { top: 10, right: 8, left: 0, bottom: 10 } : { top: 20, right: 30, left: 30, bottom: 20 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        {grouping === 'daily' ? (
                            // A label per month: '1 jun' shown as 'Jun'
                            <XAxis
                                dataKey="period"
                                ticks={monthStartTicks(rows)}
                                tickFormatter={(period: string) => period.charAt(2).toUpperCase() + period.slice(3)}
                            />
                        ) : (
                            <XAxis dataKey="period" />
                        )}
                        <YAxis
                            // An explicit undefined width overrides recharts' default (60) under React 19 and nothing is drawn
                            width={isSmallScreen ? 40 : 60}
                            label={isSmallScreen ? undefined : { value: axisLabel, angle: -90, position: 'insideLeft', offset: -15 }}
                        />
                        <Tooltip
                            formatter={(value: number, name: string) => [
                                `${value.toLocaleString('sv-SE', { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ''}`,
                                name,
                            ]}
                        />
                        <Legend wrapperStyle={{ paddingTop: '12px' }} />
                        {years.map((year, index) => (
                            <Line
                                key={year}
                                type="monotone"
                                dataKey={String(year)}
                                name={String(year)}
                                stroke={SERVICE_COLORS[index % SERVICE_COLORS.length]}
                                strokeWidth={2}
                                dot={false}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </>
    );
};

export default YearComparisonChart;
